// What the ops phone needs to hand boxes to a partner at the hub (05 §5.2, 12 §12.5): which
// partners there are, which boxes are waiting for whom, and a warning for a scanned unit before
// the server has spoken. The live queries are the shared ones in `partners.ts`, `config.ts` and
// `movement.ts`; this file only joins and judges what they give. Pure functions, no Firestore.
import { activeHolds, partnerKindOf, type PartnerKind } from '@ecobox/domain';
import type { BoxRow, ShipmentRow } from './counter';
import type { ConfigEntry } from './config';
import type { Precheck } from './ops-movement';

/** A partner a handover can be opened for: a hauler (sea and air boxes) or a courier (Prime). */
export interface OpsPartner {
  id: string;
  name: string;
  kind: PartnerKind;
}

/** Active haulers and couriers, by name. Other kinds of partner (brokers, truckers) take no handovers. */
export function toOpsPartners(entries: readonly ConfigEntry<'partner'>[]): OpsPartner[] {
  const out: OpsPartner[] = [];
  for (const { id, record } of entries) {
    const kind = partnerKindOf(record.type);
    if (kind && record.active) out.push({ id, name: record.name, kind });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

export interface WaitingUnit {
  unitId: string;
  trackingNo: string;
  cityId: string;
  cityName: string;
  /** ISO time of the box's last scan: how long it has been waiting. */
  since: string;
  /** Paid in full. An unpaid box would be refused at the server. */
  paid: boolean;
  /** Plain words for why this unit cannot go now, or undefined. */
  problem?: string;
}

const PAID: ReadonlySet<string> = new Set(['paid', 'overpaid']);

/**
 * Boxes at the hub grouped by the partner whose coverage says they deliver the city (12 §12.3):
 * the coverage map decides, not the partner stored on the shipment when it was booked, because a
 * city's partner can change until the boxes are handed over. Couriers carry Prime pieces, which
 * are not in this list. A box whose shipment the phone cannot read is left out: scanning it still
 * works and the server decides.
 */
export function waitingByPartner(args: {
  boxes: readonly BoxRow[];
  shipments: readonly ShipmentRow[];
  coverage: readonly ConfigEntry<'coverage'>[];
}): Map<string, WaitingUnit[]> {
  const coverage = new Map(args.coverage.map((c) => [c.id, c.record]));
  const shipments = new Map(args.shipments.map((s) => [s.trackingNo, s]));
  const out = new Map<string, WaitingUnit[]>();
  for (const box of args.boxes) {
    if (box.status !== 'at_branch') continue;
    const shipment = shipments.get(box.trackingNo);
    if (!shipment || shipment.service === 'prime') continue;
    const cover = coverage.get(shipment.destination.cityId);
    if (cover?.mode !== 'partner' || !cover.partnerId) continue;
    const paid = PAID.has(shipment.money.status);
    const holds = activeHolds(box);
    const problem = !paid
      ? 'Not paid in full'
      : holds.length > 0
        ? `On hold (${holds.map((h) => h.type.replace(/_/g, ' ')).join(', ')})`
        : undefined;
    const list = out.get(cover.partnerId) ?? [];
    list.push({
      unitId: box.barcode,
      trackingNo: box.trackingNo,
      cityId: shipment.destination.cityId,
      cityName: shipment.consignee.cityName,
      since: box.lastEvent?.at ?? box.updatedAt,
      paid,
      ...(problem ? { problem } : {}),
    });
    out.set(cover.partnerId, list);
  }
  // Longest waiting first.
  for (const list of out.values()) list.sort((a, b) => a.since.localeCompare(b.since));
  return out;
}

const OK: Precheck = { level: 'ok', message: '' };

/**
 * A warning for a scanned unit, from what the phone already knows (the box, and the units waiting
 * for each partner). The server decides in the end (`checkUnitForHandover`): a unit the phone has
 * never read is `ok`, and nothing here refuses what only the server can know.
 */
export function precheckHandover(args: {
  barcode: string;
  box: BoxRow | undefined;
  branchId: string;
  partnerId: string;
  /** Units waiting for each partner, from `waitingByPartner`. */
  waiting: ReadonlyMap<string, readonly WaitingUnit[]>;
}): Precheck {
  const { barcode, box, branchId, partnerId, waiting } = args;
  if (box) {
    if (box.status === 'with_partner') {
      return {
        level: 'refuse',
        message: 'This box is already with a partner. A box can be with one partner at a time.',
        code: 'illegal_state',
      };
    }
    if (box.status !== 'at_branch' || box.currentBranchId !== branchId) {
      return {
        level: 'refuse',
        message: `This box is not at ${branchId} right now, so it cannot be handed over.`,
        code: 'not_here',
      };
    }
    const holds = activeHolds(box);
    if (holds.length > 0) {
      return {
        level: 'refuse',
        message: `This box is on hold (${holds.map((h) => h.type.replace(/_/g, ' ')).join(', ')}).`,
        code: 'active_hold',
      };
    }
  }
  // The phone reads only the shipments that a partner delivers, so a box for the city EcoBox delivers
  // itself has no shipment here; the box carries where it is going. A Prime piece is not a hauler's.
  const goingTo = (box as { destination?: { mode?: string } } | undefined)?.destination;
  if (box && goingTo?.mode === 'own' && !/-P\d+$/.test(barcode)) {
    return {
      level: 'refuse',
      message: 'This city is delivered by EcoBox itself, not by a hauler.',
      code: 'own_delivery_city',
    };
  }
  for (const [otherId, units] of waiting) {
    const unit = units.find((u) => u.unitId === barcode);
    if (!unit) continue;
    if (!unit.paid) {
      return {
        level: 'refuse',
        message: 'This box will be refused: not paid in full.',
        code: 'unpaid',
      };
    }
    if (otherId !== partnerId) {
      return {
        level: 'warn',
        message: `${unit.cityName} is assigned to another partner. The server will refuse it unless the coverage is changed.`,
        code: 'extra',
      };
    }
    return OK;
  }
  return OK;
}

/** Whole days a unit has waited, for "waiting 3 days". */
export function waitingDays(sinceIso: string, nowMs: number): number {
  const since = Date.parse(sinceIso);
  return Number.isNaN(since) ? 0 : Math.max(0, Math.floor((nowMs - since) / 86_400_000));
}
